import type {Node, Parent, Point} from 'unist'
import {visit} from 'unist-util-visit'
import {type InclusiveDescendant, SKIP} from 'unist-util-visit-parents'

import {isPositionedNode} from './positioned-node'

export function overlapsNode(rangeFrom: number, rangeTo: number, nodeFrom: number, nodeTo: number): boolean {
  return (
    (rangeFrom >= nodeFrom && rangeFrom <= nodeTo) ||
    (rangeTo >= nodeFrom && rangeTo <= nodeTo) ||
    (rangeFrom <= nodeFrom && rangeTo >= nodeTo)
  )
}

/**
 * Returns all the nodes that overlap the given range, including the root node (`parent`).
 */
export function getNodesOverlappingRange<T extends Node>(tree: T, from: number, to: number) {
  const result: Array<InclusiveDescendant<T>> = []

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    if (overlapsNode(from, to, node.position.start.offset, node.position.end.offset)) result.push(node)
    else return SKIP // if this node doesn't overlap the range, we know its children won't either so we can skip them
  })

  return result
}

/** 'Shallow' version of `getNodesOverlappingRange`; only returns direct descendants. */
export function getChildrenOverlappingRange<T extends Parent>(tree: T, from: number, to: number): T['children'] {
  return tree.children.filter(
    node => isPositionedNode(node) && overlapsNode(from, to, node.position?.start.offset, node.position.end.offset),
  )
}

/** Check if the node fully contains the range. */
export function containsRange(
  rangeFrom: number,
  rangeTo: number,
  nodeFrom: number,
  nodeTo: number,
  inclusive?: boolean,
): boolean {
  if (inclusive) {
    return rangeFrom >= nodeFrom && rangeTo <= nodeTo
  }
  return rangeFrom >= nodeFrom && rangeTo <= nodeTo && !(rangeFrom === nodeFrom && rangeTo === nodeTo)
}

/**
 * Returns all nodes that fully contain the given range. Inclusive of nodes that start or end at the range
 * boundaries:
 *
 * - Included: `|...range|`, `|range...|`, `|...range...|`
 * - Excluded: `range|...|`, `ra|nge..|`
 *
 * Nodes that both start _and_ end at range boundaries are included if `inclusive` is true (`|range|`).
 */
export function getNodesContainingRange<T extends Node>(tree: T, from: number, to: number, inclusive?: boolean) {
  const result: Array<InclusiveDescendant<T>> = []

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    if (containsRange(from, to, node.position.start.offset, node.position.end.offset, inclusive)) result.push(node)
    else return SKIP // If this node does not contain this range, neither can any of its descendants
  })

  return result
}

/** Check if the range fully contains the node. */
function containsNode(rangeFrom: number, rangeTo: number, nodeFrom: number, nodeTo: number): boolean {
  return nodeFrom >= rangeFrom && nodeTo <= rangeTo
}

/**
 * Returns all nodes that are fully inside the given range. Inclusive of start and end.
 * NOTE: You might expect this method to be exclusive of the end, just like `String.slice` would be. But remember that
 * unist nodes are already exclusive of their end position. So if you give the range `0,5` and get a node from `0` to
 * `5`, that node actually covers the text from indexes `0` to `4`.
 */
export function getNodesInsideRange<T extends Node>(tree: T, from: number, to: number) {
  const result: Array<InclusiveDescendant<T>> = []

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    const nodeFrom = node.position.start.offset
    const nodeTo = node.position.end.offset

    // If the range and node don't overlap, none of the descendants of this node can possibly be contained inside the range
    if (!overlapsNode(from, to, nodeFrom, nodeTo)) return SKIP

    if (containsNode(from, to, nodeFrom, nodeTo)) result.push(node)
  })

  return result
}

type FilteredInclusiveDescendant<T extends Node, FilteredTypes extends T['type']> = Exclude<
  InclusiveDescendant<T>,
  {type: FilteredTypes}
>

/** Return the lowest node in the tree at the given `offset` that is not a `text` or `delimiter` node. */
export function getNodeAtOffset<T extends Node>(
  tree: T,
  offset: number,
): FilteredInclusiveDescendant<T, 'text' | 'delimiter'> | undefined {
  let result: FilteredInclusiveDescendant<T, 'text' | 'delimiter'> | undefined = undefined

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    // Since we go from top down, the last one encountered will be the deepest node at this offset
    if (
      containsRange(offset, offset, node.position.start.offset, node.position.end.offset) &&
      node.type !== 'text' &&
      node.type !== 'delimiter'
    )
      result = node as FilteredInclusiveDescendant<T, 'text' | 'delimiter'>
    else return SKIP // If this node does not contain this offset, neither can any of its descendants
  })

  return result
}

/** Sum two `Point` objects together. Useful for getting a document-level offset from a local node-relative offset. */
export function addPoints(a: Point, b: Point): Point {
  return {
    // line and column are 1-based, so we need to subtract 1 to avoid double-counting the first line/column
    column: a.column + b.column - 1,
    line: a.line + b.line - 1,
    offset: (a.offset ?? 0) + (b.offset ?? 0),
  }
}

/** Given a start `Point` and some text, calculate the end `Point`. */
export function calculateEndPoint<P extends Point>(start: P, text: string): P {
  const lines = text.split('\n')
  const lastLine = lines.at(-1)!
  return {
    ...start,
    line: start.line + lines.length - 1,
    column: lines.length === 1 ? start.column + text.length : lastLine.length + 1,
    offset: start.offset !== undefined ? start.offset + text.length : undefined,
  }
}
